import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { GeneralResMock } from '../types/general.type'
import { HmoPlan, HmoPlanInc } from '../types/hmo.type'

type Props = {
  id?: string
  page?: number | string
  limit?: number | string
  query?: string
  sort_dir?: 'asc' | 'desc'
  hmo_id?: string
}
export const useHmoPlan = ({ id, limit, page, query, sort_dir = 'asc', hmo_id }: Props = {}) => {
  const { data: hmoPlans, isLoading: hmoIsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<HmoPlan[], HmoPlanInc>>('hmo_plans', {
        params: {
          ...(sort_dir && { sort_dir }),
          ...(hmo_id && { hmo_id }),
          ...(query && { query }),
          ...(page && { 'page[number]': page }),
          ...(limit && { 'page[size]': limit })
        }
      })
      return data
    },
    queryKey: ['hmo_plans', { limit, page, query, hmo_id, sort_dir }]
  })

  const { data: singleHmoPlan, isLoading: singleHmoIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<HmoPlan, HmoPlanInc>>(`hmo_plans/${id}`)
      return data.data
    },
    queryKey: ['hmo_plans', id]
  })

  const { mutateAsync: createHmoPlan } = useMutation({
    mutationFn: async (formRec: any) => {
      const { data } = await AXIOS_INSTANCE.post('hmo_plans', formRec, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hmo_plans'] })
      queryClient.invalidateQueries({ queryKey: ['hmos'] })
    }
  })

  const { mutateAsync: updateHmoPlan } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`hmo_plans/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['hmo_plans'] })
      queryClient.invalidateQueries({ queryKey: ['hmos'] })
      queryClient.invalidateQueries({ queryKey: ['hmo_plans', { id }] })
    }
  })

  const { mutateAsync: deleteHmoPlan } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await AXIOS_INSTANCE.delete(`hmo_plans/${id}`, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hmo_plans'] })
      queryClient.invalidateQueries({ queryKey: ['hmos'] })
    }
  })

  return {
    createHmoPlan,
    deleteHmoPlan,
    hmoIsLoading,
    hmoPlans: hmoPlans?.data,
    hmosInc: hmoPlans?.included,
    pagination: hmoPlans?.meta.pagination,
    singleHmoPlan,
    singleHmoIsLoading,
    updateHmoPlan
  }
}
